import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ToastService } from '../services';

@Injectable()
export class ToastInterceptor implements HttpInterceptor {
  constructor(private readonly toastService: ToastService) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      tap({
        next: (event: HttpEvent<unknown>) => {
          if (event instanceof HttpResponse) {
            // Only show success toast for mutating operations (POST, PUT, PATCH, DELETE)
            if (this.shouldShowSuccessToast(request.method, request.url)) {
              this.toastService.showHttpSuccess(request.method, request.url);
            }
          }
        },
        error: (error: HttpErrorResponse) => {
          // Always log errors to console (except for expected OAuth flow errors)
          if (!this.isExpectedOAuthError(error, request.url)) {
            console.error('HTTP Error:', {
              status: error.status,
              statusText: error.statusText,
              url: error.url,
              method: request.method,
              error: error.error,
            });
          }

          // Show error toast (unless it's a silent request or expected OAuth error)
          if (!this.shouldSuppressErrorToast(request, error)) {
            this.toastService.showError(error);
          }
        },
      }),
    );
  }

  /**
   * Determine if we should show a success toast for this HTTP method
   * Generally only for operations that modify data
   */
  private shouldShowSuccessToast(method: string, url?: string): boolean {
    const mutatingMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
    
    if (!mutatingMethods.includes(method.toUpperCase())) {
      return false;
    }

    if (url?.includes("s3")) {
      return false;
    }

    if (url?.includes("download-log")) {
      return false;
    }

    // Don't show success toast for filter operations (they're POST but semantic GET)
    if (url && url.includes('/filter')) {
      return false;
    }

    return true;
  }

  /**
   * Determine if we should suppress error toast for this request
   * Useful for silent/background requests or specific endpoints
   */
  private shouldSuppressErrorToast(request: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
    // Check for custom header to suppress error toasts
    if (request.headers.get('X-Suppress-Error-Toast') === 'true') {
      return true;
    }

    // Check for polling request header
    if (request.headers.has('X-Polling-Request')) {
      return true;
    }

    // Suppress 401 errors - handled by UnauthorizedInterceptor with custom logout flow
    if (error.status === 401) {
      return true;
    }

    // Suppress 404 errors for user endpoints during registration polling
    // This handles async user creation where DynamoDB record doesn't exist yet
    if (error.status === 404 && this.isUserPollingEndpoint(request.url)) {
      return true;
    }

    // Suppress for expected OAuth flow errors
    if (this.isExpectedOAuthError(error, request.url)) {
      return true;
    }

    // Suppress for certain endpoints that handle their own error messaging
    const silentEndpoints = [
      '/auth/validate',
      '/health',
      '/ping',
    ];

    return silentEndpoints.some(endpoint => request.url.includes(endpoint));
  }

  /**
   * Check if this is a user endpoint that might be used for polling during registration
   * During async user creation (SQS queue), these endpoints will return 404 until DynamoDB record is created
   */
  private isUserPollingEndpoint(url?: string): boolean {
    if (!url) return false;

    // User retrieval endpoints used during registration polling
    const pollingEndpoints = [
      '/patients/',
      '/doctors/',
      '/clinics/',
      '/psychologists/',
    ];

    // Check if it's a GET request to a user endpoint (e.g., GET /patients/{id})
    return pollingEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * Check if this is an expected OAuth flow error that shouldn't show toast
   */
  private isExpectedOAuthError(error: HttpErrorResponse, url?: string): boolean {
    // 501 errors from Google Auth endpoints are normal in OAuth flow
    if (error.status === 501 && url?.includes('google-auth')) {
      return true;
    }

    // Also suppress 400 errors with redirect_uri in Google Auth (OAuth configuration issues)
    if (error.status === 400 && url?.includes('google-auth') && 
        error.error?.toString().includes('redirect_uri')) {
      return true;
    }

    return false;
  }
}